import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Image,
    Input,
    Modal,
    Row,
    Typography,
} from 'antd';
import {
    ArrowLeftOutlined,
    AuditOutlined,
    CalendarOutlined,
    CheckOutlined,
    CloseOutlined,
    CreditCardOutlined,
    DollarOutlined,
    FilePdfOutlined,
    FileSearchOutlined,
    PhoneOutlined,
    UserOutlined,
} from '@ant-design/icons';

import useViewCityTax from './useViewCityTax';
import Loading from '../../../components/Loading';
import PDFItem from '../components/PDFItem';
import StatusOnView from '../components/StatusOnView';
import StatusTag from '../components/StatusTag';
import PageHeader from '../../../components/PageHeader';
import { ThemeContext } from '../../../contexts/ThemeContext';
import { convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../utils/Conversores';

const { Text } = Typography;

export default function ViewCityTax() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        id,
        navigate,
        isLoading,
        isSubmiting,
        control,
        files,
        handleApproveCityTax,
        handleRefuseCityTax,
        errors,
        cityTax,
        isConfirmApproveTaxModalVisible,
        isConfirmRefuseTaxModalVisible,
        handleConfirmApproveCityTax,
        handleConfirmRefuseCityTax,
        handleCancel,
    } = useViewCityTax();

    const isPending = cityTax?.status === 'Em análise';

    return (
        <Loading loading={isLoading}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Breadcrumb Bar */}
                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        border: `1px solid ${isDark ? '#262626' : '#f0f0f0'}`,
                        padding: '10px 16px',
                        borderRadius: 8,
                    }}
                >
                    <Breadcrumb
                        items={[
                            {
                                title: <Link to="/impostos/municipais">Impostos Municipais</Link>,
                            },
                            {
                                title: `Detalhes do Imposto #${cityTax?.id ?? id ?? ''}`,
                            },
                        ]}
                    />
                </div>

                {/* Page Header */}
                <PageHeader
                    icon={<AuditOutlined />}
                    iconBg="linear-gradient(135deg, #1677ff 0%, #0958d9 100%)"
                    title={cityTax ? `Imposto de ${cityTax.app_user?.name || 'Contribuinte'}` : 'Visualizar Imposto'}
                    tag={cityTax ? <StatusTag status={cityTax.status} message={cityTax.message} /> : undefined}
                    subtitle="Consulte os detalhes do lançamento, dados do contribuinte, comprovantes anexados e emita o parecer fiscal"
                    actions={
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/impostos/municipais')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 500,
                                height: 38,
                                borderRadius: 8,
                            }}
                        >
                            Voltar para Impostos
                        </Button>
                    }
                />

                {/* Status History Alert Banner (if analyzed) */}
                {cityTax && <StatusOnView cityTax={cityTax} />}

                <Form layout="vertical">
                    {/* Card 1: Dados do Contribuinte */}
                    <Card
                        style={{
                            borderRadius: 12,
                            border: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`,
                            background: isDark ? '#141414' : '#ffffff',
                            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.03)',
                            marginBottom: 14,
                        }}
                        bodyStyle={{ padding: '20px 24px' }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <UserOutlined style={{ color: '#1677ff', fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? '#f0f0f0' : '#1e293b', display: 'block' }}>
                                    Dados do Contribuinte
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Identificação cadastral do cidadão titular do imposto
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={10}>
                                <Form.Item label="Nome do Cidadão" style={{ marginBottom: 0 }}>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                disabled
                                                size="large"
                                                prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                                                style={{ borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={7}>
                                <Form.Item label="CPF" style={{ marginBottom: 0 }}>
                                    <Controller
                                        name="cpf"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                disabled
                                                size="large"
                                                style={{ borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={7}>
                                <Form.Item label="Telefone" style={{ marginBottom: 0 }}>
                                    <Controller
                                        name="phone"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                disabled
                                                size="large"
                                                prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />}
                                                style={{ borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* Card 2: Informações do Tributo */}
                    <Card
                        style={{
                            borderRadius: 12,
                            border: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`,
                            background: isDark ? '#141414' : '#ffffff',
                            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.03)',
                            marginBottom: 14,
                        }}
                        bodyStyle={{ padding: '20px 24px' }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <DollarOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? '#f0f0f0' : '#1e293b', display: 'block' }}>
                                    Informações do Tributo
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Valores, tipo de imposto, modalidade e data de vencimento
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item label="Tipo do Imposto" style={{ marginBottom: 0 }}>
                                    <Controller
                                        name="type"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                disabled
                                                size="large"
                                                style={{ borderRadius: 8, fontWeight: 600 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item label="Modalidade de Pagamento" style={{ marginBottom: 0 }}>
                                    <Controller
                                        name="payment_modality"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                disabled
                                                size="large"
                                                prefix={<CreditCardOutlined style={{ color: '#94a3b8' }} />}
                                                style={{ borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            {cityTax?.installment ? (
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item label="Número da Parcela" style={{ marginBottom: 0 }}>
                                        <Controller
                                            name="installment"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    disabled
                                                    size="large"
                                                    style={{ borderRadius: 8 }}
                                                />
                                            )}
                                        />
                                    </Form.Item>
                                </Col>
                            ) : null}

                            <Col xs={24} sm={12} md={cityTax?.installment ? 4 : 6}>
                                <Form.Item label="Valor Declarado" style={{ marginBottom: 0 }}>
                                    <Controller
                                        name="value"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                disabled
                                                size="large"
                                                style={{
                                                    borderRadius: 8,
                                                    fontWeight: 700,
                                                    color: '#1677ff',
                                                }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={cityTax?.installment ? 4 : 6}>
                                <Form.Item label="Data de Vencimento" style={{ marginBottom: 0 }}>
                                    <Controller
                                        name="due_date"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                value={field.value ? new Date(field.value).toLocaleDateString('pt-BR') : '--'}
                                                disabled
                                                size="large"
                                                prefix={<CalendarOutlined style={{ color: '#94a3b8' }} />}
                                                style={{ borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    {/* Card 3: Documentos & Comprovantes */}
                    <Card
                        style={{
                            borderRadius: 12,
                            border: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`,
                            background: isDark ? '#141414' : '#ffffff',
                            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.03)',
                            marginBottom: isPending ? 14 : 0,
                        }}
                        bodyStyle={{ padding: '20px 24px' }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <FilePdfOutlined style={{ color: '#fa8c16', fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? '#f0f0f0' : '#1e293b', display: 'block' }}>
                                    Comprovantes & Documentos Anexados
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Arquivos enviados pelo cidadão para validação fiscal
                                </Text>
                            </div>
                        </div>

                        {files.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
                                <Image.PreviewGroup>
                                    {files
                                        .filter((file) => file.type !== 'application/pdf')
                                        .map((file, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    border: `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                                                    borderRadius: 10,
                                                    padding: 6,
                                                    background: isDark ? '#1f1f1f' : '#f8fafc',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Image
                                                    width={160}
                                                    height={160}
                                                    src={file.url}
                                                    style={{
                                                        objectFit: 'cover',
                                                        borderRadius: 8,
                                                    }}
                                                />
                                            </div>
                                        ))}
                                </Image.PreviewGroup>

                                {files
                                    .filter((file) => file.type === 'application/pdf')
                                    .map((file, index) => (
                                        <PDFItem document={file} key={index} />
                                    ))}
                            </div>
                        ) : (
                            <div
                                style={{
                                    padding: '24px 16px',
                                    textAlign: 'center',
                                    background: isDark ? '#1f1f1f' : '#f8fafc',
                                    borderRadius: 8,
                                    border: `1px dashed ${isDark ? '#303030' : '#d9d9d9'}`,
                                }}
                            >
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Não há documentos anexados a esta solicitação.
                                </Text>
                            </div>
                        )}
                    </Card>

                    {/* Card 4: Parecer & Análise Fiscal (Only if Pending) */}
                    {isPending && (
                        <Card
                            style={{
                                borderRadius: 12,
                                border: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`,
                                background: isDark ? '#141414' : '#ffffff',
                                boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.03)',
                            }}
                            bodyStyle={{ padding: '20px 24px' }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 16,
                                    paddingBottom: 10,
                                    borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                                }}
                            >
                                <FileSearchOutlined style={{ color: '#722ed1', fontSize: 18 }} />
                                <div>
                                    <Text strong style={{ fontSize: 14, color: isDark ? '#f0f0f0' : '#1e293b', display: 'block' }}>
                                        Análise & Parecer Fiscal
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                                        Insira observações ou justificativas e decida sobre a aprovação ou recusa do imposto
                                    </Text>
                                </div>
                            </div>

                            <Form.Item
                                label="Observação / Justificativa"
                                validateStatus={errors.observation ? 'error' : undefined}
                                help={errors.observation?.message}
                            >
                                <Controller
                                    name="observation"
                                    control={control}
                                    render={({ field }) => (
                                        <Input.TextArea
                                            {...field}
                                            rows={3}
                                            placeholder="Digite uma observação para a análise (obrigatória em caso de recusa)..."
                                            style={{ borderRadius: 8 }}
                                        />
                                    )}
                                />
                            </Form.Item>

                            <Divider style={{ margin: '16px 0' }} />

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                                <Button
                                    danger
                                    type="primary"
                                    icon={<CloseOutlined />}
                                    disabled={isSubmiting}
                                    loading={isSubmiting}
                                    onClick={handleRefuseCityTax}
                                    style={{
                                        height: 38,
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                    }}
                                >
                                    Recusar Imposto
                                </Button>

                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    disabled={isSubmiting}
                                    loading={isSubmiting}
                                    onClick={handleApproveCityTax}
                                    style={{
                                        height: 38,
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a',
                                    }}
                                >
                                    Aprovar Imposto
                                </Button>
                            </div>
                        </Card>
                    )}
                </Form>

                {/* Modal de Aprovação */}
                <Modal
                    title="Confirmar Aprovação do Imposto"
                    open={isConfirmApproveTaxModalVisible}
                    onOk={handleConfirmApproveCityTax}
                    okButtonProps={{
                        loading: isSubmiting,
                        disabled: isSubmiting,
                        style: { backgroundColor: '#52c41a', borderColor: '#52c41a' },
                    }}
                    cancelButtonProps={{
                        disabled: isSubmiting,
                    }}
                    okText="Aprovar Imposto"
                    cancelText="Cancelar"
                    onCancel={handleCancel}
                    centered
                >
                    <div style={{ padding: '12px 0' }}>
                        <p style={{ margin: 0, fontSize: 14 }}>
                            Deseja realmente aprovar o lançamento de imposto do contribuinte{' '}
                            <strong>"{cityTax?.app_user?.name}"</strong> no valor de{' '}
                            <strong style={{ color: '#52c41a' }}>
                                R$ {convertMoedaBRDoJSComExtamente2CasasDecimais(cityTax?.value)}
                            </strong>
                            ?
                        </p>
                        <p style={{ marginTop: 8, fontSize: 13, color: isDark ? '#a6a6a6' : '#64748b' }}>
                            O cidadão será notificado e os pontos e bilhetes correspondentes serão gerados conforme as regras do programa.
                        </p>
                    </div>
                </Modal>

                {/* Modal de Recusa */}
                <Modal
                    title="Confirmar Recusa do Imposto"
                    open={isConfirmRefuseTaxModalVisible}
                    onOk={handleConfirmRefuseCityTax}
                    okButtonProps={{
                        danger: true,
                        loading: isSubmiting,
                        disabled: isSubmiting,
                    }}
                    cancelButtonProps={{
                        disabled: isSubmiting,
                    }}
                    okText="Recusar Imposto"
                    cancelText="Cancelar"
                    onCancel={handleCancel}
                    centered
                >
                    <div style={{ padding: '12px 0' }}>
                        <p style={{ margin: 0, fontSize: 14 }}>
                            Deseja realmente recusar o lançamento de imposto do contribuinte{' '}
                            <strong style={{ color: '#ff4d4f' }}>"{cityTax?.app_user?.name}"</strong>?
                        </p>
                        <p style={{ marginTop: 8, fontSize: 13, color: isDark ? '#a6a6a6' : '#64748b' }}>
                            A justificativa informada no campo de observação será exibida ao cidadão no aplicativo.
                        </p>
                    </div>
                </Modal>
            </div>
        </Loading>
    );
}
